import { Badge, Card, OverflowText } from "ui";
import { useWatch } from "../../../features/form";
import { methodIcon } from "../SelectPaymentMethod";
import { snakeCaseToHumanReadable } from "../../../utils/snakeCaseToHumanReadable";
import { RoutingMethods } from "../../../features/routing/types/api";
import { CloneableComponents } from "./types";
import { AccountChip } from "../../AccountChip";
import { ReactNode } from "react";

type ReviewStepProps = {
  cloneableComponents?: CloneableComponents;
};

export const ReviewStep = ({ cloneableComponents }: ReviewStepProps) => {
  const name = useWatch({ name: "name" });
  const account = useWatch({ name: "account" });
  const routingRules = useWatch({ name: "routing_rules" }) || {};
  const riskRulesEnabled = useWatch({ name: "risk_rules_enabled" });
  const binLookupEnabled = useWatch({ name: "bin_lookup_enabled" });

  if (!cloneableComponents) return null;

  const selectedRoutingMethods = Object.entries(routingRules)
    .filter(([, selected]) => selected)
    .map(([method]) => method as RoutingMethods);

  return (
    <Card
      disableHover
      className="divide-y divide-foundations-subtle border-foundations-subtle bg-foundations-subtelst-02 p-0"
    >
      <PreviewRow label="Flow name" value={name} />

      <PreviewRow
        label="Assigned account"
        value={
          account ? (
            <div className="flex">
              <AccountChip account={account} />
            </div>
          ) : undefined
        }
      />

      {selectedRoutingMethods.length > 0 && (
        <div className="space-y-2 p-4">
          <span className="text-xs font-medium uppercase text-foundations-bold">
            Routing Rules
          </span>
          <ul className="flex flex-wrap items-center gap-2">
            {selectedRoutingMethods.map((method) => (
              <li key={method}>
                <div className="flex items-center gap-1.5 py-2">
                  {methodIcon[method]}
                  <span className="text-sm capitalize text-foundations-bolder">
                    {snakeCaseToHumanReadable(method.toLowerCase())}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {riskRulesEnabled && cloneableComponents.risk_rules.items.length > 0 && (
        <div className="space-y-2 p-4">
          <span className="text-xs font-medium uppercase text-foundations-bold">
            Risk Rules
          </span>
          <ul className="flex flex-wrap items-center gap-2">
            {cloneableComponents.risk_rules.items.map((rule) => (
              <li key={rule.id}>
                <Badge
                  shape="square"
                  className="bg-foundations-subtelst-02 px-2 py-1 text-xs font-medium text-foundations-bold"
                >
                  {rule.name}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {binLookupEnabled && cloneableComponents.bin_lookup.items.length > 0 && (
        <div className="space-y-2 p-4">
          <span className="text-xs font-medium uppercase text-foundations-bold">
            BIN Lookup
          </span>
          <ul className="flex flex-wrap items-center gap-2">
            {cloneableComponents.bin_lookup.items.map((item) => (
              <li key={item.service_id}>
                <Badge
                  shape="square"
                  className="flex max-w-60 items-center gap-1.5 bg-foundations-subtelst-02 px-2 py-1 text-xs font-medium text-foundations-bold"
                >
                  <img
                    src={item.service_logo}
                    alt={item.service_name}
                    width={24}
                    height={24}
                    className="rounded object-contain"
                  />
                  <OverflowText>{item.service_name}</OverflowText>
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

const PreviewRow = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className="flex flex-col gap-4 p-4">
    <span className="whitespace-nowrap text-xs font-medium uppercase text-foundations-bold">
      {label}
    </span>
    {typeof value === "string" ? (
      <span className="min-w-0 break-all text-sm font-medium text-foundations-bolder">
        {value || "—"}
      </span>
    ) : (
      value
    )}
  </div>
);
